import { ClockCircleOutlined, CloudDownloadOutlined, FileZipOutlined, PrinterOutlined } from '@ant-design/icons';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { App, Button, Descriptions, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useAuth } from 'react-oidc-context';
import { type ImportType, RequestStatus } from '../../../graphql/generated/graphql.ts';
import { useAisExport } from '../../../hooks/useAisExport.ts';
import { useLocalExport } from '../../../hooks/useLocalExport.ts';
import { usePrintDoctorLabel } from '../../../hooks/usePrintDoctorLabel.ts';
import { PrintButton } from '../../../PrintButton.tsx';
import { SHORT_DATETIME_FORMAT } from '../../../utils/dateFormatUtils.ts';
import { hasSomeRole, Role } from '../../../utils/user.ts';
import { CostsSummary } from '../CostsSummary.tsx';
import type { Order, Request } from './useRequestDetailsQuery.tsx';

export const OrderTab = ({
  request,
  showPrices,
  importType,
  order,
  hasDoctorLabel,
  doctorId,
  aisExportEnabled,
  onRefetchRequested,
}: {
  request: Request;
  showPrices: boolean;
  importType: ImportType | null | undefined;
  order: Order;
  hasDoctorLabel: boolean;
  doctorId: string;
  aisExportEnabled: boolean;
  onRefetchRequested: () => void;
}) => {
  const { message } = App.useApp();
  const auth = useAuth();
  const printDoctorLabel = usePrintDoctorLabel();
  const aisExport = useAisExport();
  const { exportOrderZip } = useLocalExport();
  const localExportAllowed = hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN], auth.user);

  const diagnoses = [...new Set(order.orderParameters.map(param => param.diagnose))].filter(it => it !== '').join('\n');

  const printDocLabel = () =>
    printDoctorLabel(
      {
        doctorIdForTemplate: doctorId,
        requestId: request.id,
        orderId: order.id,
      },
      1,
      () => message.error('Beim Drucken des Zuweiseretiketts ist ein Fehler aufgetreten'),
      () => message.success('Druckauftrag erstellt')
    );

  const orderAisExport = () => {
    aisExport(
      request,
      [order.id],
      msg => message.error(`Beim AIS-Export ist ein Fehler aufgetreten: ${msg}`),
      () => {
        message.success('Dokumente für das AIS wurden erfolgreich exportiert');
        onRefetchRequested();
      }
    );
  };

  const orderZipExport = async () => {
    try {
      await exportOrderZip(order.id);
      message.success('Auftrag-Dateien wurden erfolgreich exportiert');
      onRefetchRequested();
    } catch (_error) {
      message.error('Beim Auftrag-Export ist ein Fehler aufgetreten');
    }
  };

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      {(hasDoctorLabel || aisExportEnabled || localExportAllowed) && (
        <Space>
          {hasDoctorLabel && (
            <PrintButton icon={<PrinterOutlined />} onClick={() => printDocLabel()}>
              Zuweiseretikett drucken
            </PrintButton>
          )}
          {aisExportEnabled && (
            <Button icon={<CloudDownloadOutlined />} disabled={!window.nativeApi} onClick={() => orderAisExport()}>
              AIS-Export
            </Button>
          )}
          {localExportAllowed && (
            <Button icon={<FileZipOutlined />} disabled={!window.nativeApi} onClick={() => orderZipExport()}>
              Auftrag lokal exportieren
            </Button>
          )}
        </Space>
      )}
      <Descriptions column={1} size="small" bordered styles={{ label: { width: '200px' } }}>
        {window._env_.ENV_TAG !== 'prod' && (
          <Descriptions.Item label="Auftrag-UUID">
            <Typography.Text copyable code>
              {order.id}
            </Typography.Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Auftragsnummer">
          <span className={monoFont}>{order.number}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Erstellt am">{dayjs(order.createdAt).format(SHORT_DATETIME_FORMAT)}</Descriptions.Item>
        <Descriptions.Item label="Erstellt von">
          {order.createdByFirstName} {order.createdByLastName}
        </Descriptions.Item>
        {request.status !== RequestStatus.LOCALLY_GENERATED && (
          <Descriptions.Item label="Empfangen am">
            {order.receivedAt ? (
              dayjs(order.receivedAt).format(SHORT_DATETIME_FORMAT)
            ) : (
              <Typography.Text type="secondary">Das Labor hat den Auftrag noch nicht erhalten</Typography.Text>
            )}
          </Descriptions.Item>
        )}
        {order.aisExportedAt && (
          <Descriptions.Item label="Letzter AIS-Export">{dayjs(order.aisExportedAt).format(SHORT_DATETIME_FORMAT)}</Descriptions.Item>
        )}
        {order.lastLocalExport && (
          <Descriptions.Item label="Letzter lokaler Export">{dayjs(order.lastLocalExport).format(SHORT_DATETIME_FORMAT)}</Descriptions.Item>
        )}
        {order.acute && (
          <Descriptions.Item label="Akut angefordert">
            <ClockCircleOutlined /> Ja
          </Descriptions.Item>
        )}
        {order.elgaOptOut && <Descriptions.Item label="Situatives ELGA Opt-out">Ja</Descriptions.Item>}
        {!!order.diagnose && (
          <Descriptions.Item label="Diagnosen und Informationen für die Befundvalidierung">
            <pre style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{order.diagnose}</pre>
          </Descriptions.Item>
        )}
        {!!order.labInfo && <Descriptions.Item label="Informationen ans Labor">{order.labInfo}</Descriptions.Item>}
        {!!diagnoses && (
          <Descriptions.Item label="Diagnosen für die Verrechnung mit der Kasse">
            <pre style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{diagnoses}</pre>
          </Descriptions.Item>
        )}
        {showPrices && !importType && (
          <Descriptions.Item label="Kosten">
            <CostsSummary
              entries={order.orderParameters.map(it => ({
                billingType: it.billingType,
                price: it.price ?? null,
                text: it.specialRateShortName,
                pricePatient: null,
              }))}
              showPriceSuggestion={false}
              compact
            />
          </Descriptions.Item>
        )}
      </Descriptions>
    </Space>
  );
};
